import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNotify, type Identifier } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import {
  parseWorkLocation,
  TEAM_MAP_CITIES,
  workLocationLabel,
} from "@/modules/teamMap/teamMapCities";

const NONE = "none";
const byCountry = TEAM_MAP_CITIES.reduce<Record<string, string[]>>(
  (groups, location) => {
    (groups[location.country] ??= []).push(workLocationLabel(location));
    return groups;
  },
  {},
);

/** Profile → where you work, which places your desk on the Team Map. */
export const ProfileWorkLocationCard = ({
  memberId,
  workLocation,
}: {
  memberId: Identifier;
  workLocation: unknown;
}) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const current = parseWorkLocation(workLocation);
  const [value, setValue] = useState(
    current ? workLocationLabel(current) : NONE,
  );

  const save = async (next: string) => {
    const location =
      TEAM_MAP_CITIES.find((entry) => workLocationLabel(entry) === next) ??
      null;
    setValue(next);
    // Members update their own row (RLS organization_members_update_same_org).
    const { error } = await supabase
      .from("organization_members")
      .update({ work_location: location })
      .eq("id", memberId);
    if (error) {
      notify("Could not save your location", { type: "error" });
      return;
    }
    void queryClient.invalidateQueries({ queryKey: ["organization_members"] });
    notify(location ? "Location saved" : "Location removed", {
      type: "success",
    });
  };

  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div>
          <h2 className="text-sm font-medium">Work location</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Puts your desk on the Team Map in the Dashboard and shows your local
            time to the team.
          </p>
        </div>
        <Select value={value} onValueChange={(next) => void save(next)}>
          <SelectTrigger className="w-full" aria-label="Work location">
            <SelectValue placeholder="Choose your city" />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            <SelectItem value={NONE}>Not on the map</SelectItem>
            {Object.entries(byCountry).map(([country, labels]) => (
              <SelectGroup key={country}>
                <SelectLabel>{country}</SelectLabel>
                {labels.map((label) => (
                  <SelectItem key={label} value={label}>
                    {label.replace(`, ${country}`, "")}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </CardContent>
    </Card>
  );
};
